/**
 * Matches Web Component
 * Displays upcoming matches from Sportlink API in a table layout
 */

import {
  Match,
  MatchGroup,
  CarouselSlide,
  groupByDate,
  buildApiUrl,
  getTeamInitials,
  filterTodayRemainingFromGroups,
  buildCarouselSlides,
} from './match-data-service.js';
import { CarouselController, CarouselState } from './carousel-controller.js';

class Matches extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['client-id', 'days-ahead', 'theme', 'mode', 'rows-per-page'];
  }

  private readonly shadowRoot_: ShadowRoot;
  private hasInitialized = false;
  private carouselController: CarouselController | null = null;

  constructor() {
    super();
    this.shadowRoot_ = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
    this.fetchMatches();
    this.hasInitialized = true;
  }

  disconnectedCallback(): void {
    this.destroyCarousel();
  }

  attributeChangedCallback(): void {
    if (this.hasInitialized) {
      this.destroyCarousel();
      this.fetchMatches();
    }
  }

  private destroyCarousel(): void {
    if (this.carouselController) {
      this.carouselController.destroy();
      this.carouselController = null;
    }
  }

  get clientId(): string | null {
    return this.getAttribute('client-id');
  }

  get daysAhead(): number {
    return Number.parseInt(this.getAttribute('days-ahead') || '7', 10);
  }

  get theme(): 'light' | 'dark' {
    const attr = this.getAttribute('theme');
    return attr === 'dark' ? 'dark' : 'light';
  }

  get mode(): 'default' | 'narrow' {
    const attr = this.getAttribute('mode');
    return attr === 'narrow' ? 'narrow' : 'default';
  }

  get rowsPerPage(): number {
    return Number.parseInt(this.getAttribute('rows-per-page') || '10', 10);
  }

  private getApiUrl(): string | null {
    const clientId = this.clientId;
    if (!clientId) {
      return null;
    }
    return buildApiUrl(clientId, this.daysAhead);
  }

  private async fetchMatches(): Promise<void> {
    const apiUrl = this.getApiUrl();

    if (!apiUrl) {
      this.showError('Geen client-id opgegeven');
      return;
    }

    try {
      this.showLoading();
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data: Match[] = await response.json();

      if (this.mode === 'narrow') {
        this.renderNarrowMode(data);
      } else {
        const groups = groupByDate(data);
        this.renderMatchGroups(groups);
      }
    } catch (error) {
      this.showError((error as Error).message);
    }
  }

  private renderNarrowMode(allMatches: Match[]): void {
    const container = this.shadowRoot_.querySelector('.container');
    if (!container) return;

    // Group matches by date, filter today's past matches, build slides
    const groups = groupByDate(allMatches);
    const filteredGroups = filterTodayRemainingFromGroups(groups);
    const slides = buildCarouselSlides(filteredGroups, this.rowsPerPage);

    if (slides.length === 0) {
      container.innerHTML = '<div class="empty">Geen wedstrijden gevonden.</div>';
      return;
    }

    // Initialize carousel controller
    this.carouselController = new CarouselController(10000, (state: CarouselState) => {
      this.updateCarouselDisplay(state);
    });
    this.carouselController.init(slides.length);

    // Render carousel HTML
    container.innerHTML = this.renderCarouselHtml(slides);

    // Start auto-advance
    this.carouselController.start();
  }

  private renderCarouselHtml(slides: CarouselSlide[]): string {
    const slidesHtml = slides
      .map(
        (slide, index) => `
      <div class="carousel-slide" data-slide="${index}">
        <h3 class="slide-date-header">${this.escapeHtml(slide.dateLabel)}</h3>
        <table>
          <thead>
            <tr>
              <th class="col-time">Tijd</th>
              <th class="col-team">Thuis</th>
              <th class="col-vs"></th>
              <th class="col-team">Uit</th>
              <th class="col-field">Veld</th>
              <th class="col-locker">Kleedkamers</th>
            </tr>
          </thead>
          <tbody>
            ${slide.matches.map((match) => this.renderMatchRow(match)).join('')}
          </tbody>
        </table>
      </div>
    `
      )
      .join('');

    const dotsHtml =
      slides.length > 1
        ? `<div class="carousel-dots">
          ${slides.map((_: CarouselSlide, index: number) => `<span class="carousel-dot${index === 0 ? ' active' : ''}" data-dot="${index}"></span>`).join('')}
        </div>`
        : '';

    return `
      <div class="carousel">
        <div class="carousel-track">
          ${slidesHtml}
        </div>
        ${dotsHtml}
      </div>
    `;
  }

  private updateCarouselDisplay(state: CarouselState): void {
    const track = this.shadowRoot_.querySelector('.carousel-track') as HTMLElement;
    if (track) {
      track.style.transform = `translateX(-${state.currentSlide * 100}%)`;
    }

    // Update dots and progress
    const dots = this.shadowRoot_.querySelectorAll<HTMLElement>('.carousel-dot');
    dots.forEach((dot, index) => {
      const isActive = index === state.currentSlide;
      dot.classList.toggle('active', isActive);
      if (isActive) {
        dot.style.setProperty('--progress', `${state.progress * 100}%`);
      }
    });
  }

  private formatTime(timeString: string): string {
    return timeString || '';
  }

  private render(): void {
    this.shadowRoot_.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

          /* Light theme (default) */
          --bg-primary: #ffffff;
          --bg-secondary: #f9fafb;
          --bg-hover: #f3f4f6;
          --text-primary: #1f2937;
          --text-secondary: #374151;
          --text-muted: #6b7280;
          --text-vs: #9ca3af;
          --border-color: #e5e7eb;
          --border-strong: #d1d5db;
          --initials-bg: #e5e7eb;
          --initials-text: #374151;
          --error-bg: #fef2f2;
          --error-text: #dc2626;
        }

        :host([theme="dark"]) {
          --bg-primary: #111827;
          --bg-secondary: #1f2937;
          --bg-hover: #374151;
          --text-primary: #f9fafb;
          --text-secondary: #e5e7eb;
          --text-muted: #9ca3af;
          --text-vs: #6b7280;
          --border-color: #374151;
          --border-strong: #4b5563;
          --initials-bg: #374151;
          --initials-text: #e5e7eb;
          --error-bg: #450a0a;
          --error-text: #fca5a5;
        }

        * {
          box-sizing: border-box;
        }

        .container {
          max-width: 100%;
          overflow: hidden;
          background: var(--bg-primary);
          color: var(--text-primary);
          padding: 1rem;
          border-radius: 0.5rem;
        }

        .loading, .error, .empty {
          text-align: center;
          padding: 2rem;
          color: var(--text-muted);
        }

        .error {
          color: var(--error-text);
          background: var(--error-bg);
          border-radius: 0.5rem;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(
            0.8rem,
            1vw + 0.5rem,
            2rem
          );
          table-layout: fixed;
        }

        thead {
          background: var(--bg-secondary);
        }

        th {
          padding: 0.75rem 0.5rem;
          text-align: left;
          font-weight: 600;
          color: var(--text-secondary);
          border-bottom: 2px solid var(--border-color);
          white-space: nowrap;
        }

        td {
          padding: 0.75rem 0.5rem;
          border-bottom: 1px solid var(--border-color);
          vertical-align: middle;
        }

        tr:hover {
          background: var(--bg-hover);
        }

        .col-time {
          width: 60px;
          white-space: nowrap;
          font-weight: 500;
        }

        .col-team {
          width: 30%;
        }

        .col-vs {
          width: 20px;
          text-align: center;
          color: var(--text-vs);
          padding: 0.75rem 0.25rem;
        }

        .col-field {
          width: 100px;
          white-space: nowrap;
        }

        .col-locker {
          width: 100px;
          white-space: nowrap;
        }

        .team-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .team-logo {
          width: 24px;
          height: 24px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .team-initials {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--initials-bg);
          color: var(--initials-text);
          font-size: 0.625rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .day-section {
          margin-bottom: 1.5rem;
        }

        .day-header {
          font-weight: 600;
          color: var(--text-secondary);
          padding: 0.5rem 0;
          font-size: clamp(
            0.8rem,
            1vw + 0.5rem,
            2rem
          );
          border-bottom: 2px solid var(--border-color);
          margin-bottom: 0;
        }

        /* Carousel styles for narrow mode */
        .carousel {
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .carousel-track {
          display: flex;
          transition: transform 0.5s ease-in-out;
        }

        .carousel-slide {
          flex: 0 0 100%;
          min-width: 100%;
        }

        .carousel-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 0;
        }

        .carousel-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--border-color);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .carousel-dot.active {
          width: 40px;
          height: 10px;
          border-radius: 5px;
          background: var(--border-strong);
          position: relative;
          overflow: hidden;
        }

        .carousel-dot.active::after {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: var(--progress, 0%);
          background: var(--text-secondary);
          border-radius: 5px;
          transition: width 0.05s linear;
        }

        .carousel-dot:hover {
          background: var(--text-muted);
        }

        .carousel-dot.active:hover {
          background: var(--border-strong);
        }

        .slide-date-header {
          font-weight: 600;
          color: var(--text-secondary);
          padding: 1rem;
          font-size: clamp(
            0.8rem,
            1vw + 0.5rem,
            2rem
          );
          border-bottom: 2px solid var(--border-color);
          margin: 0;
        }
      </style>
      <div class="container">
        <div class="loading">Wedstrijden laden...</div>
      </div>
    `;
  }

  private showLoading(): void {
    const container = this.shadowRoot_.querySelector('.container');
    if (container) {
      container.innerHTML = '<div class="loading">Wedstrijden laden...</div>';
    }
  }

  private showError(message: string): void {
    const container = this.shadowRoot_.querySelector('.container');
    if (container) {
      container.innerHTML = `<div class="error">Fout bij laden: ${this.escapeHtml(message)}</div>`;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private renderMatchGroups(groups: MatchGroup[]): void {
    const container = this.shadowRoot_.querySelector('.container');
    if (!container) return;

    if (groups.length === 0) {
      container.innerHTML = '<div class="empty">Geen wedstrijden gevonden.</div>';
      return;
    }

    container.innerHTML = groups.map((group) => this.renderDaySection(group)).join('');
  }

  private renderDaySection(group: MatchGroup): string {
    return `
      <div class="day-section">
        <h3 class="day-header">${this.escapeHtml(group.dateLabel)}</h3>
        <table>
          <thead>
            <tr>
              <th class="col-time">Tijd</th>
              <th class="col-team">Thuis</th>
              <th class="col-vs"></th>
              <th class="col-team">Uit</th>
              <th class="col-field">Veld</th>
              <th class="col-locker">Kleedkamers</th>
            </tr>
          </thead>
          <tbody>
            ${group.matches.map((match) => this.renderMatchRow(match)).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderMatchRow(match: Match): string {
    const lockers = this.formatLockers(match.kleedkamerthuisteam, match.kleedkameruitteam);
    return `
      <tr>
        <td class="col-time">${this.escapeHtml(this.formatTime(match.aanvangstijd))}</td>
        <td class="col-team">
          <div class="team-cell">
            ${this.renderTeamLogo(match.thuisteamlogo, match.thuisteam)}
            <span>${this.escapeHtml(match.thuisteam || '-')}</span>
          </div>
        </td>
        <td class="col-vs">-</td>
        <td class="col-team">
          <div class="team-cell">
            ${this.renderTeamLogo(match.uitteamlogo, match.uitteam)}
            <span>${this.escapeHtml(match.uitteam || '-')}</span>
          </div>
        </td>
        <td class="col-field">${this.escapeHtml(match.veld || '-')}</td>
        <td class="col-locker">${this.escapeHtml(lockers)}</td>
      </tr>
    `;
  }

  private renderTeamLogo(logoUrl: string, teamName: string): string {
    const initials = getTeamInitials(teamName);
    const fallbackBadge = `<span class="team-initials">${this.escapeHtml(initials)}</span>`;

    if (!logoUrl) {
      return fallbackBadge;
    }

    // Use onerror to replace broken images with initials badge
    return `<img
      class="team-logo"
      src="${this.escapeHtml(logoUrl)}"
      alt=""
      onerror="this.style.display='none';this.nextElementSibling.style.display='flex';"
    /><span class="team-initials" style="display:none;">${this.escapeHtml(initials)}</span>`;
  }

  private formatLockers(home: string, away: string): string {
    if (!home && !away) return '-';
    if (home && away) return `${home} / ${away}`;
    return home || away;
  }
}

customElements.define('sportlink-matches', Matches);
